import { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { LanguageEnum } from "graphql/types";
import { IntlContext, getIsValidLanguage } from "providers/i18n";
import { getLocalePrefix } from "utils";

export default (): null => {
  const { currentLocale, setLocale } = useContext(IntlContext);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const [_, version, locale] = pathname.split("/");

    // Redirect outdated routes starting with locale
    if (getIsValidLanguage(version)) {
      const lang = version;

      // Redirect outdated email routes
      if (pathname.includes("public-document")) {
        navigate(
          `/v2/${lang}/signmore/public-document/${
            pathname.split("public-document/")[1]
          }`
        );
      }

      if (pathname.includes("sign-document")) {
        navigate(
          `/v2/${lang}/signmore/sign-document/${
            pathname.split("sign-document/")[1]
          }`
        );
        return;
      }

      if (pathname.includes("login/magic-link/verification")) {
        navigate(`/v2/${lang}/login/magic-link/verification${search}`);
        return;
      }
    }

    // Redirect invalid routes
    const isV2 = version === "v2";
    const isValidLocale = getIsValidLanguage(locale);
    if (!isV2 || !isValidLocale) {
      // Bare /{lang}/… URL (e.g. the marketing "Sign in" link /nb/login): keep
      // its own language and full path, just move it under /v2. The old code
      // dropped the path segment and forced currentLocale, so /nb/login landed
      // on /v2/en/common/home — a spinner for anyone with a stale session.
      const langPrefix = getIsValidLanguage(version)
        ? `/${version}`
        : getLocalePrefix(currentLocale);
      navigate(
        `/v2${langPrefix}/${pathname.split("/").slice(2).join("/")}${search}`
      );
      return;
    }

    // Update locale from pathname
    const validLocale = locale?.toUpperCase() as LanguageEnum;
    if (currentLocale !== validLocale) setLocale(validLocale);
  }, [pathname]);

  return null;
};
